import { useState } from "react";
import { Button, Upload, Typography, message, Space } from "antd";
import { UploadOutlined, PictureOutlined, DeleteOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";

const { Text } = Typography;
const { Dragger } = Upload;

interface LogoUploaderProps {
  currentLogoUrl?: string | null;
  onLogoUpload: (file: File) => Promise<boolean>;
  isUploading?: boolean;
}

export default function LogoUploader({ currentLogoUrl, onLogoUpload, isUploading = false }: LogoUploaderProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl || null);

  const validateFile = (file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return "Formato de arquivo inválido. Formatos aceitos: JPG, PNG, SVG, WEBP";
    }
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      return "O arquivo é muito grande. Tamanho máximo: 2MB";
    }
    return null;
  };

  const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
    const actualFile = file as File;
    const validationError = validateFile(actualFile);

    if (validationError) {
      message.error(validationError);
      onError?.(new Error(validationError));
      return;
    }

    const objectUrl = URL.createObjectURL(actualFile);
    setPreviewUrl(objectUrl);

    try {
      const success = await onLogoUpload(actualFile);
      if (!success) {
        setPreviewUrl(currentLogoUrl || null);
        onError?.(new Error("Falha no upload"));
      } else {
        onSuccess?.("ok");
      }
    } catch (error) {
      setPreviewUrl(currentLogoUrl || null);
      message.error("Erro ao fazer upload do logo");
      console.error("Erro ao fazer upload do logo:", error);
      onError?.(error as Error);
    }
  };

  const handleRemoveLogo = () => {
    setPreviewUrl(null);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    customRequest,
    disabled: isUploading,
    accept: "image/jpeg,image/jpg,image/png,image/svg+xml,image/webp",
  };

  return (
    <div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, backgroundColor: '#fafafa' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Text strong style={{ marginBottom: 8 }}>Logo da Empresa</Text>

        {previewUrl ? (
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 160,
              height: 112,
              marginBottom: 12,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff'
            }}>
              <img
                src={previewUrl}
                alt="Logo da empresa"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>

            <Space>
              <Upload {...uploadProps}>
                <Button size="small" icon={<UploadOutlined />} disabled={isUploading} loading={isUploading}>
                  Alterar
                </Button>
              </Upload>

              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveLogo}
                disabled={isUploading}
              >
                Remover
              </Button>
            </Space>
          </div>
        ) : (
          <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Dragger {...uploadProps} style={{ width: '100%', padding: '24px 0' }}>
              <p className="ant-upload-drag-icon">
                <PictureOutlined style={{ fontSize: 32, color: '#1677ff' }} />
              </p>
              <p className="ant-upload-text">Clique ou arraste para fazer upload</p>
            </Dragger>
          </div>
        )}

        <Text type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
          Formatos suportados: JPG, PNG, SVG, WEBP (máx. 2MB)
        </Text>
      </div>
    </div>
  );
} 